import React from 'react';
import { ChevronsRight, Asterisk, CircleCheck, ShieldCheck, Award, MoveUpRight, Network } from 'lucide-react';
import { ABOUT_IMAGE_1 } from '../data/mock';

const skills = ['Advisory', 'Certification', 'Documentation', 'Testing coordination', 'Portal filing', 'Renewals & AMC'];

const groupBrands = [
  {
    src: '/assets/brands/complise-india.jpeg',
    alt: 'Complise India',
    number: '01',
    eyebrow: 'India compliance',
    description: 'Regulatory approvals, certification and managed compliance.',
    accent: 'bg-[#C41E3A]',
    tint: 'from-rose-50/90',
    imageClass: 'w-[94%] max-w-none h-auto',
  },
  {
    src: '/assets/brands/complise-global.png',
    alt: 'Complise Global',
    number: '02',
    eyebrow: 'Global access',
    description: 'International standards and market-entry coordination.',
    accent: 'bg-[#1258B5]',
    tint: 'from-blue-50/90',
    imageClass: 'w-[94%] h-auto object-contain',
  },
  {
    src: '/assets/brands/recycle-veda.jpeg',
    alt: 'Recycle Veda',
    number: '03',
    eyebrow: 'Circular future',
    description: 'Responsible recycling and sustainability-led operations.',
    accent: 'bg-[#188331]',
    tint: 'from-emerald-50/90',
    imageClass: 'w-[98%] max-w-none h-auto',
  },
];

const About = () => {
  return (
    <section id="about" className="relative py-16 lg:py-24 bg-[#FFF8EC]/40 overflow-hidden">
      <div className="absolute -left-36 top-10 w-80 h-80 rounded-full bg-rose-100/50 blur-3xl pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* One focused, professional team image */}
          <div className="lg:col-span-6">
            <div className="relative min-h-[440px] lg:min-h-[620px] rounded-[2rem] overflow-hidden shadow-[0_28px_80px_-35px_rgba(10,14,26,0.45)] group">
              <img
                src={ABOUT_IMAGE_1}
                alt="Compliance specialists reviewing product documentation"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A]/85 via-[#0A0E1A]/10 to-transparent" />
              <div className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg">
                <MoveUpRight className="w-5 h-5 text-[#C41E3A]" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/75 mb-3">
                  <span className="w-7 h-px bg-[#FCA5A5]" /> Regulatory execution desk
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold max-w-md leading-tight">
                  Advice, documentation and authority coordination in one accountable team.
                </h3>
              </div>
            </div>
          </div>

          {/* Right: content - flex col to align top/bottom nicely */}
          <div className="lg:col-span-6 flex flex-col">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-[#C41E3A] mb-4">
              <Asterisk className="w-4 h-4" /> Who We Are
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-neutral-900 leading-[1.05] mb-5">
              Comprehensive solution for compliance excellence
            </h2>
            <p className="text-neutral-600 text-base md:text-lg leading-relaxed mb-6">
              A Service by <span className="font-semibold text-neutral-900">Sharma Brother&rsquo;s</span> — Complise India has evolved from focused certification advisory into a wider compliance execution system for approvals, factory coordination, renewals and managed operations.
            </p>

            {/* Feature checklist */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {[
                { icon: ShieldCheck, text: 'Authority-led approval mapping' },
                { icon: Award, text: 'Since 2018, across 30+ industries' },
                { icon: CircleCheck, text: 'Timeline focused delivery' },
                { icon: ShieldCheck, text: 'AMC compliance ownership' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                    <f.icon className="w-4 h-4 text-[#C41E3A]" />
                  </span>
                  <span className="text-sm text-neutral-800 font-medium">{f.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <a
                href="#about"
                className="pill-btn group inline-flex items-center gap-2 rounded-full bg-[#C41E3A] hover:bg-[#A81830] text-white pl-6 pr-1 h-12 text-sm font-semibold"
              >
                More About Us
                <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center pill-arrow">
                  <ChevronsRight className="w-3.5 h-3.5 text-[#C41E3A]" />
                </span>
              </a>
            </div>

            <div className="relative mb-7 overflow-hidden rounded-[2rem] border border-neutral-200/90 bg-white shadow-[0_24px_64px_-38px_rgba(15,23,42,0.42)]">
              <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-rose-100/60 blur-3xl pointer-events-none" />

              <div className="relative flex flex-col gap-4 border-b border-neutral-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between md:px-6">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-rose-100 bg-rose-50 text-[#C41E3A]">
                    <Network className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="block text-[9px] font-extrabold uppercase tracking-[0.22em] text-[#C41E3A]">Sharma Brother&rsquo;s ecosystem</span>
                    <strong className="mt-1 block text-sm font-bold text-neutral-900">One group. Three focused brands.</strong>
                  </div>
                </div>

                <div className="relative h-14 w-full overflow-hidden rounded-xl border border-neutral-100 bg-white sm:w-[210px]" aria-label="Parent identity">
                  <img
                    src="/assets/brands/sharma-brothers.jpeg"
                    alt="Sharma Brother's"
                    className="absolute left-1/2 top-1/2 w-[108%] max-w-none -translate-x-1/2 -translate-y-1/2"
                  />
                </div>
              </div>

              <div className="relative px-4 pb-4 pt-7 md:px-5 md:pb-5">
                <div className="absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-neutral-200" />
                <span className="absolute left-1/2 top-3 h-2.5 w-2.5 -translate-x-1/2 rounded-full border-2 border-white bg-[#C41E3A] shadow-[0_0_0_1px_rgba(196,30,58,0.2)]" />

                <div className="grid overflow-hidden rounded-[1.4rem] border border-neutral-200 bg-neutral-50 sm:grid-cols-3" aria-label="Sharma Brother's group brands">
                  {groupBrands.map((brand, index) => (
                    <article
                      key={brand.alt}
                      className={`group relative min-w-0 bg-gradient-to-br ${brand.tint} to-white px-4 py-4 ${index ? 'border-t border-neutral-200 sm:border-l sm:border-t-0' : ''}`}
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-neutral-500">{brand.eyebrow}</span>
                        <span className="text-[10px] font-black tabular-nums text-neutral-300">{brand.number}</span>
                      </div>

                      <div className="relative h-20 overflow-hidden rounded-xl border border-white/90 bg-white shadow-[0_10px_26px_-20px_rgba(15,23,42,0.45)]">
                        <img
                          src={brand.src}
                          alt={brand.alt}
                          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-transform duration-500 group-hover:scale-[1.03] ${brand.imageClass}`}
                        />
                      </div>

                      <div className="mt-3 flex items-start gap-2.5">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${brand.accent}`} />
                        <p className="m-0 text-[11px] leading-[1.55] text-neutral-600">{brand.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom capability strip */}
            <div className="mt-auto pt-5 border-t border-neutral-200">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Core capabilities</div>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((s) => (
                  <span key={s} className="text-[11px] px-2.5 py-1 rounded-md bg-white border border-neutral-200 text-[#C41E3A] font-semibold">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
